import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, fmt, fmtDate, getUser, ORDER_STATUS, stBadge , toUsd } from '../api';
import { useToast, Loading, Empty, ErrorBox, rowNav, Modal, Eyebrow } from '../ui';
import { Icons } from '../icons';

const CUR_SYM: Record<string, string> = { CNY: '¥', USD: '$', UZS: "so'm" };
const sumsText = (sums: Record<string, number> | undefined) => {
  if (!sums || Object.keys(sums).length === 0) return '—';
  return Object.entries(sums).map(([c, v]) => `${fmt(v, 2)} ${CUR_SYM[c] || c}`).join(' + ');
};
/** Aralash valyutalarni bitta $ ga keltirib taqqoslash oson bo'lsin. */
const sumsUsd = (sums: Record<string, number> | undefined) => {
  if (!sums || Object.keys(sums).length === 0) return '';
  let total = 0, ok = false;
  for (const [c, v] of Object.entries(sums)) {
    const u = toUsd(v, c);
    if (u == null) return '';
    total += u; ok = true;
  }
  return ok && total > 0 ? '≈ $' + fmt(total, 0) : '';
};

const PAGE_SIZE = 50;

export default function Orders() {
  const [list, setList] = useState<any[] | null>(null);
  const [onec, setOnec] = useState<any>(null);
  const [onecBusy, setOnecBusy] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reload, setReload] = useState(0);
  const nav = useNavigate();
  const toast = useToast();
  const user = getUser();
  const canEdit = ['admin', 'zakaz_operator'].includes(user?.role);

  const load = (p = page) => {
    const qs = new URLSearchParams({ page: String(p), pageSize: String(PAGE_SIZE) });
    if (status) qs.set('status', status);
    if (search) qs.set('search', search);
    if (dateFrom) qs.set('dateFrom', dateFrom);
    if (dateTo) qs.set('dateTo', dateTo);
    setList(null); setErr('');
    api.get('/orders?' + qs.toString())
      .then((d) => { setList(d.items); setTotal(d.total); })
      .catch((e) => { setErr(e.message); setList([]); });
  };
  useEffect(() => { load(); }, [status, dateFrom, dateTo, page, reload]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const goPage = (p: number) => setPage(p);
  const applyFilter = (fn: () => void) => { fn(); setPage(1); };

  const create = async () => {
    try {
      const r = await api.post('/orders', { orderDate: new Date().toISOString() });
      nav('/orders/' + r.id);
    } catch (e: any) { toast(e.message, true); }
  };
  const remove = async (e: React.MouseEvent, o: any) => {
    e.stopPropagation();
    if (!confirm(`${o.number} zakaz o'chirilsinmi?`)) return;
    try { await api.del('/orders/' + o.id); toast("O'chirildi"); load(); }
    catch (err: any) { toast(err.message, true); }
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Zakazlar</h1>
          <p className="page-sub">{list ? total : '—'} ta zakaz · Xitoy zavodlariga buyurtmalar</p>
        </div>
        <div className="head-actions">
          {canEdit && (
            <button className="btn" onClick={() => setOnec({ start: '', end: '' })}>
              <Icons.download size={16} /> 1C dan tortish
            </button>
          )}
          {canEdit && <button className="btn btn-primary" onClick={create}><Icons.plus size={16} /> Yangi zakaz</button>}
        </div>
      </div>

      <form className="filters" onSubmit={(e) => { e.preventDefault(); page === 1 ? load(1) : setPage(1); }}>
        <input className="search" placeholder="Raqam yoki artikul bo'yicha qidirish…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input" style={{ width: 190 }} value={status} onChange={(e) => applyFilter(() => setStatus(e.target.value))}>
          <option value="">Barcha statuslar</option>
          {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input className="input" style={{ width: 150 }} type="date" title="Sanadan" value={dateFrom} onChange={(e) => applyFilter(() => setDateFrom(e.target.value))} />
        <input className="input" style={{ width: 150 }} type="date" title="Sanagacha" value={dateTo} onChange={(e) => applyFilter(() => setDateTo(e.target.value))} />
        <button className="btn">Qidirish</button>
        {(status || search || dateFrom || dateTo) && (
          <button type="button" className="btn" onClick={() => { setStatus(''); setSearch(''); setDateFrom(''); setDateTo(''); setPage(1); setReload((n) => n + 1); }}>Tozalash</button>
        )}
      </form>

      {err ? <ErrorBox text={err} onRetry={() => load()} /> : !list ? <Loading /> : list.length === 0 ? (
        <Empty text="Zakazlar yo'q" hint={canEdit ? '«+ Yangi zakaz» tugmasi bilan birinchi zakazni oching' : undefined} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Raqam</th><th>Sana</th><th>Zavod</th><th className="num">Poz.</th><th className="num">Dona</th><th className="num">Mesta</th><th className="num">Kg</th><th className="num">m³</th><th className="num">Summa</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {list.map((o) => (
                <tr key={o.id} style={{ cursor: 'pointer' }} onClick={rowNav(() => nav('/orders/' + o.id))}>
                  <td className="link code">{o.number}</td>
                  <td>{fmtDate(o.orderDate)}</td>
                  <td>{o.supplier?.name || '—'}</td>
                  <td className="num">{o.positions}</td>
                  <td className="num">{fmt(o.totalUnits)}</td>
                  <td className="num">{fmt(o.totalBoxes, 1)}</td>
                  <td className="num">{fmt(o.totalKg, 1)}</td>
                  <td className="num">{fmt(o.totalM3, 2)}</td>
                  <td className="num">
                    {sumsText(o.sums)}
                    {sumsUsd(o.sums) && <div className="muted" style={{ fontSize: 11.5 }}>{sumsUsd(o.sums)}</div>}
                  </td>
                  <td><span className={'badge ' + stBadge(ORDER_STATUS, o.status).cls}>{stBadge(ORDER_STATUS, o.status).label}</span></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {canEdit && <button className="act act-del" title="O'chirish" onClick={(e) => remove(e, o)}><Icons.trash size={16} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!!list?.length && totalPages > 1 && (
        <div className="filters" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn btn-sm" disabled={page <= 1} onClick={() => goPage(page - 1)}>‹</button>
          <span className="muted">{page} / {totalPages}</span>
          <button className="btn btn-sm" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>›</button>
        </div>
      )}

      {onec && (
        <Modal title="1C dan xaridlarni tortish" onClose={() => setOnec(null)} large>
          <p className="muted" style={{ marginTop: 0 }}>
            <b>Alight Group</b> ning <b>Alight Xitoy Sklad</b> iga kelgan xaridlar
            <b> zakaz</b> hujjatiga aylantiriladi: tovar zavoddan olingan, lekin hali
            Xitoyda. O'zbekistonga yetib kelgani <b>Kirim</b> bo'limida qayd etiladi,
            zakaz bilan farqi esa <b>yetmovchilik</b> bo'lib chiqadi.
          </p>
          <p className="muted">
            Kargo, bojxona, soliq va valyuta hujjatlari olinmaydi — ular xarajat.
            «Qo'qon diller» ga ketgani boshqa tashkilotniki va tortilmaydi.
          </p>

          <div className="grid-2">
            <div className="field">
              <label>Sanadan (bo'sh — oxirgi 30 kun)</label>
              <input className="input" type="date" value={onec.start} onChange={(e) => setOnec({ ...onec, start: e.target.value })} />
            </div>
            <div className="field">
              <label>Sanagacha</label>
              <input className="input" type="date" value={onec.end} onChange={(e) => setOnec({ ...onec, end: e.target.value })} />
            </div>
          </div>

          {onec.result && (
            <div className="card" style={{ padding: 12, marginBottom: 10 }}>
              <Eyebrow title="Natija" desc={`1C da ${onec.result.feedDocs} hujjat, shundan bizniki ${onec.result.matched} ta`} />
              <table style={{ marginTop: 8 }}>
                <tbody>
                  <tr><td>Yangi zakaz</td><td className="num"><b>{onec.result.created}</b></td></tr>
                  <tr><td>Yangilandi</td><td className="num">{onec.result.updated}</td></tr>
                  <tr><td>Qatorlar</td><td className="num">{onec.result.lines}</td></tr>
                  <tr><td>Yangi zavod ochildi</td><td className="num">{onec.result.newSuppliers?.length ?? 0}</td></tr>
                </tbody>
              </table>
              {!!onec.result.unknownArticles?.length && (
                <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                  Katalogda topilmagan artikullar: {onec.result.unknownArticles.length} ta — bu qatorlar tashlab yuborildi.
                  Katalogni «1C dan yangilash» bilan to'ldiring.
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button className="btn" onClick={() => setOnec(null)}>Yopish</button>
            <button className="btn btn-primary" disabled={onecBusy} onClick={async () => {
              setOnecBusy(true);
              try {
                const qs = new URLSearchParams();
                if (onec.start) qs.set('start', onec.start);
                if (onec.end) qs.set('end', onec.end);
                const r = await api.post('/onec/purchases/sync' + (qs.toString() ? '?' + qs : ''));
                setOnec({ ...onec, result: r });
                toast(`${r.created} yangi zakaz, ${r.updated} yangilandi`);
                load(1);
              } catch (e: any) { toast(e.message, true); }
              finally { setOnecBusy(false); }
            }}>{onecBusy ? 'Tortilmoqda…' : 'Tortish'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
